import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Input } from 'antd';
import { getMedicineAlternatives } from '../../../../service/api/prescription';
import './medicine-alternatives.scss';

const EMPTY = { total: 0, items: [] };

// Body of a prescribed row's "Alternatives" popover: other brands with the same
// generic, the exact same type and the same strength, so picking one only changes
// the brand. It is mounted only while the popover is open — nothing loads until
// the doctor asks — and the search runs on the server.
const MedicineAlternatives = ({ row, onPick }) => {
    const { user } = useSelector((state) => state.auth);
    const [search, setSearch] = useState('');
    const [result, setResult] = useState(EMPTY);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const timeoutRef = useRef(null);
    const requestRef = useRef(0);

    // sponsored doctors see their manufacturer first, as in the medicine search
    const sponsor = user?.user?.details?.sponsor == 'true';
    const manufacturerName = user?.user?.organization?.manufacturerName || '';

    const requestParams = (text, offset) => {
        const params = {
            generic: row?.generic || '',
            type: row?.type || '',
            power: row?.power || '',
            medicineId: row?.medicine || '',
            search: (text || '').trim(),
            offset,
        };
        if (sponsor) {
            params.manufacturerName = manufacturerName;
            params.medicinePriority = user?.user?.organization?.medicinePriority;
        }
        return params;
    };

    // first page (on open / for a new search)
    const load = async (text) => {
        const requestId = ++requestRef.current;
        setIsLoading(true);
        setIsLoadingMore(false);
        const res = await getMedicineAlternatives(requestParams(text, 0)).catch(() => null);
        if (requestId !== requestRef.current) return;
        setResult(res?.status === 200 ? res.data || EMPTY : EMPTY);
        setIsLoading(false);
    };

    // next page, appended to the list
    const loadMore = async () => {
        const requestId = ++requestRef.current;
        setIsLoadingMore(true);
        const res = await getMedicineAlternatives(requestParams(search, result.items.length)).catch(() => null);
        // a search started meanwhile -> its results win
        if (requestId !== requestRef.current) return;
        if (res?.status === 200) {
            setResult((prev) => ({
                total: res.data?.total ?? prev.total,
                items: [...prev.items, ...(res.data?.items || [])],
            }));
        }
        setIsLoadingMore(false);
    };

    useEffect(() => {
        load('');
        return () => clearTimeout(timeoutRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (text) => {
        setSearch(text);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => load(text), 400);
    };

    const isSponsorBrand = (medicine) => sponsor && manufacturerName && (medicine?.manufacturer || '').startsWith(manufacturerName);
    const items = result?.items || [];

    return (
        <div className="medicine-alternatives">
            <div className="medicine-alternatives__head">
                <span>Alternatives</span>
                <small title={`${row?.generic || ''} · ${row?.type || ''}${row?.power ? ` · ${row.power}` : ''}`}>
                    {row?.generic}{row?.type ? ` · ${row.type}` : ''}{row?.power ? ` · ${row.power}` : ''}
                </small>
            </div>

            <Input
                size="small"
                allowClear
                autoFocus
                placeholder="Search brand or company"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
            />

            {/* keep the current list on screen while a search reloads it */}
            <div className={`medicine-alternatives__list ${isLoading && items.length ? 'is-loading' : ''}`}>
                {isLoading && !items.length ? (
                    <div className="medicine-alternatives__state">Loading…</div>
                ) : !items.length ? (
                    <div className="medicine-alternatives__state">
                        {search.trim() ? 'No brand matches that search.' : 'No other brand with the same generic, type and strength.'}
                    </div>
                ) : (
                    items.map((medicine) => (
                        <button
                            type="button"
                            key={medicine._id}
                            className={`medicine-alternatives__item ${isSponsorBrand(medicine) ? 'same_org' : ''}`}
                            onClick={() => onPick(medicine)}
                        >
                            {/* brand + strength on one line; long strengths end in "…" (full text on hover) */}
                            <span className="medicine-alternatives__line" title={`${medicine.name}${medicine.power ? ` · ${medicine.power}` : ''}`}>
                                <span className="medicine-alternatives__name">{medicine.name}</span>
                                {medicine.power && <span className="medicine-alternatives__power">{medicine.power}</span>}
                            </span>
                            <span className="medicine-alternatives__maker">{medicine.manufacturer}</span>
                        </button>
                    ))
                )}
            </div>

            {!isLoading && result?.total > items.length && (
                <div className="medicine-alternatives__more">
                    <span>Showing {items.length} of {result.total}</span>
                    <button type="button" onClick={loadMore} disabled={isLoadingMore}>
                        {isLoadingMore ? 'Loading…' : `Load more (${result.total - items.length})`}
                    </button>
                </div>
            )}
        </div>
    );
};

export default MedicineAlternatives;
